import { alpha, createTheme } from '@mui/material/styles';

import type {} from '@mui/lab/themeAugmentation';

import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';

export const APP_BAR_HEIGHT = 8;
export const TAB_BAR_HEIGHT = 6;

declare module '@mui/material/styles' {
  interface Palette {
    tertiary: Palette['primary'];
  }

  interface PaletteOptions {
    inactive?: PaletteOptions['primary'];
    unpublished?: PaletteOptions['primary'];
    tertiary?: PaletteOptions['primary'];
    surface?: PaletteOptions['primary'];
    outline?: PaletteOptions['primary'];
    test?: PaletteOptions['primary'];
    emulator?: PaletteOptions['primary'];
  }

  interface PaletteColor {
    on?: string;
    container?: string;
    onContainer?: string;
    outline?: string;
    outlineVariant?: string;
    dim?: string;
    tint?: string;
    variant?: string;
    onVariant?: string;
    bright?: string;
    containerLowest?: string;
    containerLow?: string;
    containerHigh?: string;
    containerHighest?: string;
  }

  interface SimplePaletteColorOptions {
    container?: string;
    onContainer?: string;
    outline?: string;
    outlineVariant?: string;
    fixed?: string;
    onFixed?: string;
    fixedDim?: string;
    onFixedVariant?: string;
    inverse?: string;
    onInverse?: string;
    dim?: string;
    tint?: string;
    variant?: string;
    onVariant?: string;
    bright?: string;
  }

  interface PaletteBackgroundChannel {
    default: string;
    on?: string;
  }
    interface TypographyVariants {
    headline: React.CSSProperties;
    title: React.CSSProperties;
    h6dense: React.CSSProperties;
    label: React.CSSProperties;
    pdense: React.CSSProperties;
    iconbutton: React.CSSProperties;
    displayLarge: React.CSSProperties;
    displayMedium: React.CSSProperties;
    displaySmall: React.CSSProperties;
    headlineLarge: React.CSSProperties;
    headlineMedium: React.CSSProperties;
    headlineSmall: React.CSSProperties;
    titleLarge: React.CSSProperties;
    titleMedium: React.CSSProperties;
    titleSmall: React.CSSProperties;
    brandSmall: React.CSSProperties;
    brandMedium: React.CSSProperties;
    brandLarge: React.CSSProperties;
    labelLarge: React.CSSProperties;
    labelMedium: React.CSSProperties;
    labelSmall: React.CSSProperties;
    bodyLarge: React.CSSProperties;
    bodyMedium: React.CSSProperties;
    bodySmall: React.CSSProperties;
  }

  // headline (h5) - top of the page
  // title (h6) - page section titles
  // h6dense (h6) - (dense variant)
  // pdense (p) - (dense variant)
  // iconbutton (body1) - navigation button label

  interface TypographyVariants {
    headline: React.CSSProperties;
    title: React.CSSProperties;
    h6dense: React.CSSProperties;
    label: React.CSSProperties;
    pdense: React.CSSProperties;
    iconbutton: React.CSSProperties;
    displayLarge: React.CSSProperties;
    displayMedium: React.CSSProperties;
    displaySmall: React.CSSProperties;
    headlineLarge: React.CSSProperties;
    headlineMedium: React.CSSProperties;
    headlineSmall: React.CSSProperties;
    titleLarge: React.CSSProperties;
    titleMedium: React.CSSProperties;
    titleSmall: React.CSSProperties;
    brandSmall: React.CSSProperties;
    brandMedium: React.CSSProperties;
    brandLarge: React.CSSProperties;
    labelLarge: React.CSSProperties;
    labelMedium: React.CSSProperties;
    labelSmall: React.CSSProperties;
    bodyLarge: React.CSSProperties;
    bodyMedium: React.CSSProperties;
    bodySmall: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    headline?: React.CSSProperties;
    title?: React.CSSProperties;
    h6dense?: React.CSSProperties;
    label?: React.CSSProperties;
    pdense?: React.CSSProperties;
    iconbutton?: React.CSSProperties;
    displayLarge?: React.CSSProperties;
    displayMedium?: React.CSSProperties;
    displaySmall?: React.CSSProperties;
    headlineLarge?: React.CSSProperties;
    headlineMedium?: React.CSSProperties;
    headlineSmall?: React.CSSProperties;
    titleLarge?: React.CSSProperties;
    titleMedium?: React.CSSProperties;
    titleSmall?: React.CSSProperties;
    brandSmall?: React.CSSProperties;
    brandMedium?: React.CSSProperties;
    brandLarge?: React.CSSProperties;
    labelLarge?: React.CSSProperties;
    labelMedium?: React.CSSProperties;
    labelSmall?: React.CSSProperties;
    bodyLarge?: React.CSSProperties;
    bodyMedium?: React.CSSProperties;
    bodySmall?: React.CSSProperties;
  }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    headline: true;
    title: true;
    h6dense: true;
    label: true;
    pdense: true;
    iconbutton: true;
    displayLarge: true;
    displayMedium: true;
    displaySmall: true;
    headlineLarge: true;
    headlineMedium: true;
    headlineSmall: true;
    titleLarge: true;
    titleMedium: true;
    titleSmall: true;
    brandSmall: true;
    brandMedium: true;
    brandLarge: true;
    labelLarge: true;
    labelMedium: true;
    labelSmall: true;
    bodyLarge: true;
    bodyMedium: true;
    bodySmall: true;
  }
}

const iPhoneInput = {
  styleOverrides: {
    root: {
      '*': {
        userSelect: 'text !important' /* Standard syntax */,
      },
    },
  },
};

const mui6Theme = createTheme({
  components: {
    MuiTextField: iPhoneInput,
    MuiInput: iPhoneInput,
    MuiInputBase: iPhoneInput,
    MuiFilledInput: iPhoneInput,
    MuiOutlinedInput: iPhoneInput,
    MuiAppBar: {
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            backgroundColor: 'surface.container',
            color: 'surface.contrastText',
            display: 'block',
            height: theme.spacing(APP_BAR_HEIGHT-1),
            minHeight: theme.spacing(APP_BAR_HEIGHT-1),
            my: 0,
            pb: 1,
            typography: {
              fontSize: '2rem',
            },
            '.MuiToolbar-root': {
              backgroundColor: 'surface.container',
              borderBottom: '1px solid',
              borderBottomColor: 'outline.variant',
              color: 'surface.contrastText',
              pr: '.5rem',
            },
            '.NavMenu': {
              pl: '1rem',
            },
            '.AppBarIcon': {
              color: 'surface.onVariant',
              '&:hover': {
                backgroundColor: 'surface.containerHigh',
                borderRadius: '50%',
              },
            },
            '.AppBarText': {
              color: 'surface.onVariant',
              fontFamily: 'sans-serif',
              fontSize: { xs: '.8rem', sm: '1rem', md: '1.25rem' },
              fontWeight: 'inherit',
              letterSpacing: '0rem',
              textDecoration: 'none',
            },
            '.AppBarText-home': {
              color: 'surface.onVariant',
              fontFamily: 'monospace',
              fontSize: '1rem',
              fontWeight: 700,
              letterSpacing: '.3rem',
            },
          }),
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        listbox: ({ theme }) =>
          theme.unstable_sx({
            backgroundColor: 'primary.container',
            color: 'primary.onContainer',
            '& .MuiListItem-root': {
              backgroundColor: 'primary.container',
              color: 'primary.onContainer',
              '&:hover': {
                backgroundColor: 'primary.containerHigh',
              },
              '&.Mui-focused': {
                backgroundColor: 'primary.containerHigh',
              },
            },
          }),
        paper: ({ theme }) =>
          theme.unstable_sx({
            backgroundColor: 'primary.container',
            color: 'primary.onContainer',
          }),
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            backgroundColor: 'surface.main',
            color: 'surface.contrastText',
          }),
      },
    },
    MuiButton: {
      defaultProps: {
        size: 'small',
      },
      variants: [
        {
          props: { size: 'small' },
          style: {
            height: '2.5rem',
            lineHeight: '1rem',
            minWidth: '5rem',
          },
        },
        {
          props: { size: 'medium' },
          style: {
            height: '3.5rem',
            minWidth: '7rem',
            px: '1.5rem',
          },
        },
        {
          props: { size: 'large' },
          style: {
            height: '6rem',
            minWidth: '10rem',
            px: '3rem',
          },
        },
      ],
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            borderRadius: (theme.shape.borderRadius as number) * 2,
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '1rem',
            height: '2.5rem',
            letterSpacing: '0.023rem',
            lineHeight: '1rem',
            minWidth: '5rem',
            px: '1rem',
            '&:hover': {
              backgroundColor: 'surface.containerHigh',
            },
          }),
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            color: 'surface.contrastText',
            backgroundColor: 'surface.main',
            '&.BodyContainer': {
              color: 'surface.contrastText',
              typography: 'bodyMedium',
              px: '0rem',
              pt: '0rem',
            },
          }),
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            mt: '.25rem',
            mb: '.5rem',
            borderColor: 'divider',
          }),
        middle: ({ theme }) =>
          theme.unstable_sx({
            mx: '1rem',
          }),
      },
    },
    MuiDrawer: {
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            backgroundColor: 'surface.container',
            color: 'surface.contrastText',
            borderRight: '1px solid',
            borderRightColor: 'outline.variant',
            width: '5rem',
            fontSize: '1rem',
            flexShrink: 0,
            overflow: 'auto',
            '& .MuiDrawer-paper': {
              width: '5rem',
              backgroundColor: 'surface.container',
              color: 'surface.contrastText',
              boxSizing: 'border-box',
            },
            '.MuiListItem-root': {
              backgroundColor: 'surface.container',
              color: 'surface.onContainer',
            },
            '.MuiIconButton-root': {
              typography: 'iconbutton',
              flexDirection: 'column',
              width: '100%',
              backgroundColor: 'surface.container',
              color: 'surface.onContainer',
            },
            '& .MuiIconButton-root:hover': {
              width: '100%',
            },
          }),
      },
    },
    MuiFab: {
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            position: 'fixed',
            bottom: '2rem',
            right: '2rem',
            boxShadow: 3,
            backgroundColor: 'secondary.container',
            color: 'secondary.onContainer',
            '&:hover': {
              boxShadow: 5,
              backgroundColor: 'secondary.container',
              color: 'secondary.onContainer',
            },
          }),
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            gap: theme.spacing(2),
            py: 1,
            backgroundColor: 'transparent',
            '&.Mui-selected': {
              backgroundColor: 'action.selected',
            },
            '&.test': {
              border: '2px dashed',
              borderColor: 'tertiary.main',
            },
            '&.admin': {
              backgroundColor: 'primary.container',
              color: 'primary.onContainer',
            },
            '&.inactive': {
              color: 'text.disabled',
              '& .MuiTypography-root': {
                color: 'text.disabled',
              },
              '& .MuiSvgIcon-root': {
                color: 'text.disabled',
              },
              opacity: 0.6,
            },
            '&.draft': {
              color: 'text.disabled',
              '& .MuiTypography-root': {
                color: 'text.disabled',
              },
            },
            '&.active': {
              // Active items are styled normally (default)
            },
            '&.archived': {
              color: 'text.disabled',
              '& .MuiTypography-root': {
                color: 'text.disabled',
              },
              opacity: 0.6,
            },
            '&.deleted': {
              color: 'text.disabled',
              '& .MuiTypography-root': {
                color: 'text.disabled',
              },
              opacity: 0.4,
            },
            '&.cancelled': {
              color: 'text.disabled',
              '& .MuiTypography-root': {
                color: 'text.disabled',
              },
              opacity: 0.6,
            },
          }),
      },
    },
    MuiListItemAvatar: {
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            color: 'surface.onVariant',
            '& .MuiAvatar-circular': {
              backgroundColor: 'primary.container',
              color: 'primary.onContainer',
            },
            '& .GratiDirectoryItemBadge-container': {
              position: 'absolute',
              bottom: -4,
              right: -4,
            },
            '& .GratiDirectoryItemBadge-icon': {
              fontSize: '1.2rem',
              color: 'primary.main',
              backgroundColor: 'background.paper',
              borderRadius: '50%',
            },
          }),
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            color: 'surface.onVariant',
          }),
        primary: ({ theme }) =>
          theme.unstable_sx({
            '&.title': {
              color: 'surface.contrastText',
              variant: 'title',
            },
          }),
        secondary: ({ theme }) =>
          theme.unstable_sx({
            color: 'surface.onVariant',
          }),
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            color: 'primary.onVariant',
            '&.title': {
              color: 'surface.contrastText',
              variant: 'title',
            },
          }),
      },
    },
    MuiListSubheader: {
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            color: 'surface.contrastText',
          }),
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: ({ theme }) =>
          theme.unstable_sx({
            backgroundColor: 'surface.container',
            py: 1, // 8dp vertical padding
          }),
        list: ({ theme }) =>
          theme.unstable_sx({
            py: 0, // Remove default list padding since we added it to paper
          }),
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            minHeight: '48px', // MD3 standard height
            px: 1.5, // 12dp horizontal padding
            py: 1.5, // 12dp vertical padding
            '&:hover': {
              backgroundColor: 'action.hover',
            },
            '&.Mui-selected': {
              backgroundColor: 'secondary.container',
              color: 'secondary.onContainer',
              '&:hover': {
                backgroundColor: 'secondary.container',
              },
            },
          }),
      },
    },
    MuiStack: {
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            mx: 0,
            px: 0,
            py: 0,
            '&.AppStack': {
              backgroundColor: 'surface.main',
              color: 'surface.contrastText',
              display: 'flex',
              FlexGrow: 1,
            },
            '&.TableStack': {
              my: '0rem',
              pb: '.25rem',
            },
            '&.TableStack &.TableStack': {
              my: '0rem',
              pb: '.25rem',
            },
            '&.test': {
              border: '2px dashed',
              borderColor: 'tertiary.main',
            },
          }),
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            '& .MuiSwitch-switchBase': {
              '&.Mui-checked': {
                color: 'primary.fixed',
                '& + .MuiSwitch-track': {
                  backgroundColor: 'primary.main',
                },
              },
              '&.Mui-disabled': {
                color: 'outline.main',
                '& + .MuiSwitch-track': {
                  backgroundColor: 'surface.container',
                },
              },
            },
            '& .MuiSwitch-track': {
              borderRadius: theme.spacing(1.37),
              '&::before, &::after': {
                content: '""',
                position: 'absolute',
                top: '50%',
                transform: 'translateY(-50%)',
                width: theme.spacing(2.5),
                height: theme.spacing(2.5),
              },
            },
            '& .MuiSwitch-thumb': {
              boxShadow: 'none',
              width: theme.spacing(2),
              height: theme.spacing(2),
              margin: theme.spacing(0.25),
            },
          }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            p: 0,
            bt: 0,
            borderBottom: 1,
            width: '100%',
            backgroundColor: 'surface.main',
            color: 'surface.onVariant',
            '&:hover:not(.Mui-selected)': {
              backgroundColor: 'surface.container',
              color: 'surface.contrastText',
              opacity: '0.8'
            },
            '&.Mui-selected': {
              backgroundColor: 'surface.container',
              color: 'surface.contrastText',
            },
          }),
      },
    },
    MuiTabPanel: {
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            pt: 1,
            px: 0,
            pb: 0,
          }),
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            '&.firstColumn': {
              backgroundColor: 'primary.container',
              color: 'primary.onContainer',
            },
            '& .MuiTableCell-root:last-child': {
              borderRight: 'none',
            },
            borderRightWidth: '1px',
            borderRightStyle: 'solid',
            borderRightColor: 'divider',
            padding: 1,
            minWidth: '150px',
            whiteSpace: 'nowrap',
          }),
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: 'divider',
            overflowX: 'auto',
            maxWidth: '100%',
          }),
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            '&.MuiTableRow-head': {
              backgroundColor: 'primary.container',
              color: 'primary.onContainer',
            },
            backgroundColor: 'surface.main',
            color: 'surface.contrastText',
          }),
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            backgroundColor: 'surface.main',
            color: 'surface.contrastText',
            [`& .MuiToggleButtonGroup-grouped`]: {
              '&.Mui-disabled': {
                border: 0,
              },
            },
            [`& .MuiToggleButtonGroup-middleButton, & .MuiToggleButtonGroup-lastButton`]: {
              borderLeft: '1px solid transparent',
            },
          }),
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            '&.Mui-selected': {
              color: 'secondary.onContainer',
              backgroundColor: 'secondary.container',
            },
          }),
      },
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          headline: 'h5',
          title: 'h6',
          h6dense: 'h6',
          label: 'p',
          pdense: 'p',
          iconbutton: 'p',
          displayLarge: 'h1',
          displayMedium: 'h2',
          displaySmall: 'h2',
          headlineLarge: 'h3',
          headlineMedium: 'h4',
          headlineSmall: 'h4',
          titleLarge: 'h5',
          titleMedium: 'h6',
          titleSmall: 'h6',
          brandLarge: 'h5',
          brandMedium: 'h6',
          brandSmall: 'h6',
          labelLarge: 'label',
          labelMedium: 'label',
          labelSmall: 'label',
          bodyLarge: 'p',
          bodyMedium: 'p',
          bodySmall: 'p',
        },
      },
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          ...(ownerState.component === 'p' && {
            marginBottom: theme.spacing(2),
          }),
          ...(ownerState.component === 'h3' && {
            marginTop: theme.spacing(3),
            marginBottom: theme.spacing(1),
          }),
          ...theme.unstable_sx({
            '&.title': {
              color: 'surface.contrastText',
              variant: 'title',
            },
            // Semantic color classes for typography
            '&.emphasized-text': {
              color: 'primary.main',
              fontWeight: 500,
            },
            '&.secondary-text': {
              color: 'text.secondary',
            },
            '&.muted-text': {
              color: 'text.disabled',
            },
            '&.metadata-text': {
              color: 'text.secondary',
            },
            '&.label-text': {
              color: 'text.secondary',
            },
            '&.caption-text': {
              color: 'text.secondary',
            },
            '&.error-text': {
              color: 'error.main',
            },
            '&.cancelled-text': {
              color: 'error.main',
            },
            '&.success-text': {
              color: 'success.main',
            },
            '&.warning-text': {
              color: 'warning.main',
            },
          }),
        }),
      },
    },
  },

   typography: {
    fontFamily: 'Inter, sans-serif',
    headline: {
      fontSize: '1.5rem',
      fontWeight: 500,
    },
    h6dense: {
      fontSize: '1rem',
      fontWeight: 800,
    },
    label: {
      fontSize: '.8rem',
      fontWeight: 200,
    },
    pdense: {
      fontSize: '.8rem',
      fontWeight: 300,
      textAlign: 'center',
    },
    iconbutton: {
      fontSize: '.8rem',
      fontWeight: 300,
    },
    displayLarge: {
      fontSize: '3.5625rem', // 57px
      fontWeight: 400,
      lineHeight: '4rem', // 64px
      letterSpacing: '-0.0156rem',
    },
    displayMedium: {
      fontSize: '2.8125rem', // 45px
      fontWeight: 400,
      lineHeight: '3.25rem', // 52px
    },
    displaySmall: {
      fontSize: '2.25rem', // 36px
      fontWeight: 400,
      lineHeight: '2.75rem', // 44px
    },
    headlineLarge: {
      fontSize: '2rem', // 32px
      fontWeight: 400,
      lineHeight: '2.5rem', // 40px
    },
    headlineMedium: {
      fontSize: '1.75rem', // 28px
      fontWeight: 400,
      lineHeight: '2.25rem', // 36px
    },
    headlineSmall: {
      fontSize: '1.5rem', // 24px
      fontWeight: 400,
      lineHeight: '2rem', // 32px
    },
    titleLarge: {
      fontSize: '1.375rem', // 22px
      fontWeight: 400,
      lineHeight: '1.75rem', // 28px
    },
    titleMedium: {
      fontSize: '1rem', // 16px
      fontWeight: 500,
      letterSpacing: '0.0094rem', // 0.15px
      lineHeight: '1.5rem', // 24px
    },
    titleSmall: {
      fontSize: '0.875rem', // 14px
      fontWeight: 500,
      letterSpacing: '0.0063rem', // 0.1px
      lineHeight: '1.25rem', // 20px
    },
    brandLarge: {
      fontFamily: 'Inter',
      fontSize: '1.375rem', // 22px
      fontWeight: 700,
      letterSpacing: '0.00625rem',
      lineHeight: '1.75rem', // 28px
    },
    brandMedium: {
      fontFamily: 'Inter',
      fontSize: '1rem', // 16px
      fontWeight: 700,
      letterSpacing: '0.00625rem',
      lineHeight: '1.5rem', // 24px
    },
    brandSmall: {
      fontFamily: 'Inter',
      fontSize: '1rem',
      fontWeight: 600,
      letterSpacing: '0.00625rem',
      lineHeight: '1.3331rem',
    },
    bodyLarge: {
      fontSize: '1rem', // 16px
      fontWeight: 400,
      letterSpacing: '0.031rem', // 0.5px
      lineHeight: '1.5rem', // 24px
    },
    bodyMedium: {
      fontSize: '0.875rem', // 14px
      fontWeight: 400,
      letterSpacing: '0.0156rem', // 0.25px
      lineHeight: '1.25rem', // 20px
    },
    bodySmall: {
      fontSize: '0.75rem', // 12px
      fontWeight: 400,
      letterSpacing: '0.025rem', // 0.4px
      lineHeight: '1rem', // 16px
    },
    labelLarge: {
      fontSize: '0.875rem', // 14px
      fontWeight: 500,
      letterSpacing: '0.0063rem', // 0.1px
      lineHeight: '1.25rem', // 20px
    },
    labelMedium: {
      fontSize: '0.75rem', // 12px
      fontWeight: 500,
      letterSpacing: '0.031rem', // 0.5px
      lineHeight: '1rem', // 16px
    },
    labelSmall: {
      fontSize: '0.6875rem', // 11px
      fontWeight: 500,
      letterSpacing: '0.031rem', // 0.5px
      lineHeight: '1rem', // 16px
    },
  },
  colorSchemes: {
    dark: {
      palette: {
        primary: {
          main: '#A3BFA0',
          light: alpha('#A3BFA0', 0.5),
          dark: alpha('#A3BFA0', 0.9),
          contrastText: '#FFFFFF',
          container: '#3E5537',
          onContainer: '#FFFFFF',
          fixed: '#3E5537',
          onFixed: '#FFFFFF',
          fixedDim: '#4C6744',
          onFixedVariant: '#FFFFFF',
          inverse: '#EAEAD0',
        },
        secondary: {
          main: '#B2B29F',
          light: alpha('#B2B29F', 0.5),
          dark: alpha('#B2B29F', 0.9),
          contrastText: '#FFFFFF',
          container: '#4C6744',
          onContainer: '#FFFFFF',
          fixed: '#4C6744',
          onFixed: '#FFFFFF',
          fixedDim: '#849A81',
          onFixedVariant: '#F7F5ED',
        },
        tertiary: {
          main: '#9FB2B1',
          light: alpha('#9FB2B1', 0.5),
          dark: alpha('#9FB2B1', 0.9),
          contrastText: '#FFFFFF',
          container: '#2F4443',
          onContainer: '#FFFFFF',
          fixed: '#2F4443',
          onFixed: '#FFFFFF',
          fixedDim: '#9FB2B1',
          onFixedVariant: '#FFFFFF',
        },
        error: {
          main: '#D14343',
          contrastText: '#FFFFFF',
          container: '#5A1A1A',
          onContainer: '#FFFFFF',
        },
        test: {
          main: '#FFD700',
          contrastText: '#694E00',
          container: '#936B00',
          onContainer: '#FFE999',
          variant: '#7B5B00',
          onVariant: '#FFE070',
          dim: '#4C3900',
          bright: '#FFEB3B',
        },
        emulator: {
          main: '#FF6F00',
          contrastText: '#FFFFFF',
          container: '#E65100',
          onContainer: '#FFF3E0',
        },
        background: {
          default: '#212529',
          //alt: "#2C2F33"
        },
        surface: {
          main: '#2C2F33',
          inverse: '#EAEAD0',
          onInverse: '#2C2F33',
          contrastText: '#FFFFFF',
          variant: '#424242',
          onVariant: '#EAEAD0',
          dim: '#3C3C3C',
          bright: '#4C6744',
          container: '#424242',
          // strong: "#FFFFFF",
          //onSurface: "#FFFFFF"
        },
        outline: {
          main: '#968E98',
          variant: '#D4D4D4',
        },
        divider: '#4B454D', // Same as OutlineVariant in MD3
      },
    },
    light: {
      palette: {
        primary: {
          main: '#4C6744',
          contrastText: '#FFFFFF',
          container: '#EAEAD0',
          onContainer: '#212529',
          fixed: '#EAEAD0',
          onFixed: '#212529',
          fixedDim: '#A3BFA0',
          onFixedVariant: '#3E5537',
          inverse: '#849A81',
        },
        secondary: {
          main: '#849A81',
          contrastText: '#FFFFFF',
          container: '#F7F5ED',
          onContainer: '#212529',
          fixed: '#F7F5ED',
          onFixed: '#212529',
          fixedDim: '#B2B29F',
          onFixedVariant: '#4C6744',
        },
        tertiary: {
          main: '#6C8583',
          contrastText: '#FFFFFF',
          container: '#DCE6E5',
          onContainer: '#1E2F2E',
          fixed: '#DCE6E5',
          onFixed: '#1E2F29',
          fixedDim: '#B2B29F',
          onFixedVariant: '#4C6744',
        },
        error: {
          main: '#B94747',
          contrastText: '#FFFFFF',
          container: '#FCE8E8',
          onContainer: '#410002',
        },
        test: {
          main: '#B04159',
          contrastText: '#FFFFFF',
          container: '#F3E6E8',
          onContainer: '#410011',
          variant: '#B04159', // TODO: Color not provided by Em
          onVariant: '#F3E6E8', // TODO: Color not provided by Em
          dim: '#410011', // TODO: Color not provided by Em
          bright: '#F3E6E8', // TODO: Color not provided by Em
        },
        emulator: {
          main: '#FF9100',
          contrastText: '#000000',
          container: '#FFE0B2',
          onContainer: '#E65100',
        },

        background: {
          default: '#f9f8f0',
          // alt: '#F9F8F0',
        },
        surface: {
          main: '#fcfcfc',
          inverse: '#4C6744',
          onInverse: '#fcfcfc',
          contrastText: '#212529',
          variant: '#EAEAD0',
          onVariant: '#424242',
          dim: '#EAEAD0',
          bright: '#FCFBF6',
          container: '#F0F0EE',
          //strong: '#3c3c3c',
          //onSurface: '#212529',
        },
        outline: {
          main: '#E5E5E1',
          variant: '#B0B0B0',
        },
        divider: '#E5E5E1',
      },
    },
  },

  spacing: (factor: number) => `${0.5 * factor}rem`,
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
});

export default mui6Theme;

/* 
{
  "description": "TYPE: CUSTOM\nMaterial Theme Builder export 2024-08-13 08:37:08",
  "seed": "#846D26",
  "coreColors": {
      "primary": "#846D26"
  },
  "extendedColors": [],
  "schemes": {
      "light": {
          "primary": "#735C0C",
          "surfaceTint": "#735C0C",
          "onPrimary": "#FFFFFF",
          "primaryContainer": "#FFE08A",
          "onPrimaryContainer": "#241A00",
          "secondary": "#695D3F",
          "onSecondary": "#FFFFFF",
          "secondaryContainer": "#F2E1BB",
          "onSecondaryContainer": "#231B04",
          "tertiary": "#47664A",
          "onTertiary": "#FFFFFF",
          "tertiaryContainer": "#C8ECC9",
          "onTertiaryContainer": "#03210C",
          "error": "#BA1A1A",
          "onError": "#FFFFFF",
          "errorContainer": "#FFDAD6",
          "onErrorContainer": "#410002",
          "background": "#FFF8F1",
          "onBackground": "#1F1B13",
          "surface": "#FFF8F1",
          "onSurface": "#1F1B13",
          "surfaceVariant": "#EBE1CF",
          "onSurfaceVariant": "#4C4639",
          "outline": "#7D7667",
          "outlineVariant": "#CFC6B4",
          "shadow": "#000000",
          "scrim": "#000000",
          "inverseSurface": "#343027",
          "inverseOnSurface": "#F8F0E2",
          "inversePrimary": "#E2C46D",
          "primaryFixed": "#FFE08A",
          "onPrimaryFixed": "#241A00",
          "primaryFixedDim": "#E2C46D",
          "onPrimaryFixedVariant": "#574400",
          "secondaryFixed": "#F2E1BB",
          "onSecondaryFixed": "#231B04",
          "secondaryFixedDim": "#D5C5A1",
          "onSecondaryFixedVariant": "#50462A",
          "tertiaryFixed": "#C8ECC9",
          "onTertiaryFixed": "#03210C",
          "tertiaryFixedDim": "#ADCFAE",
          "onTertiaryFixedVariant": "#2F4D34",
          "surfaceDim": "#E1D9CC",
          "surfaceBright": "#FFF8F1",
          "surfaceContainerLowest": "#FFFFFF",
          "surfaceContainerLow": "#FBF3E5",
          "surfaceContainer": "#F5EDDF",
          "surfaceContainerHigh": "#F0E7D9",
          "surfaceContainerHighest": "#EAE1D4"
      },
      "light-medium-contrast": {
          "primary": "#534100",
          "surfaceTint": "#735C0C",
          "onPrimary": "#FFFFFF",
          "primaryContainer": "#8B7223",
          "onPrimaryContainer": "#FFFFFF",
          "secondary": "#4C4226",
          "onSecondary": "#FFFFFF",
          "secondaryContainer": "#807454",
          "onSecondaryContainer": "#FFFFFF",
          "tertiary": "#2B4930",
          "onTertiary": "#FFFFFF",
          "tertiaryContainer": "#5C7C5F",
          "onTertiaryContainer": "#FFFFFF",
          "error": "#8C0009",
          "onError": "#FFFFFF",
          "errorContainer": "#DA342E",
          "onErrorContainer": "#FFFFFF",
          "background": "#FFF8F1",
          "onBackground": "#1F1B13",
          "surface": "#FFF8F1",
          "onSurface": "#1F1B13",
          "surfaceVariant": "#EBE1CF",
          "onSurfaceVariant": "#484235",
          "outline": "#655E50",
          "outlineVariant": "#817A6B",
          "shadow": "#000000",
          "scrim": "#000000",
          "inverseSurface": "#343027",
          "inverseOnSurface": "#F8F0E2",
          "inversePrimary": "#E2C46D",
          "primaryFixed": "#8B7223",
          "onPrimaryFixed": "#FFFFFF",
          "primaryFixedDim": "#705909",
          "onPrimaryFixedVariant": "#FFFFFF",
          "secondaryFixed": "#807454",
          "onSecondaryFixed": "#FFFFFF",
          "secondaryFixedDim": "#665B3D",
          "onSecondaryFixedVariant": "#FFFFFF",
          "tertiaryFixed": "#5C7C5F",
          "onTertiaryFixed": "#FFFFFF",
          "tertiaryFixedDim": "#446348",
          "onTertiaryFixedVariant": "#FFFFFF",
          "surfaceDim": "#E1D9CC",
          "surfaceBright": "#FFF8F1",
          "surfaceContainerLowest": "#FFFFFF",
          "surfaceContainerLow": "#FBF3E5",
          "surfaceContainer": "#F5EDDF",
          "surfaceContainerHigh": "#F0E7D9",
          "surfaceContainerHighest": "#EAE1D4"
      },
      "light-high-contrast": {
          "primary": "#2C2100",
          "surfaceTint": "#735C0C",
          "onPrimary": "#FFFFFF",
          "primaryContainer": "#534100",
          "onPrimaryContainer": "#FFFFFF",
          "secondary": "#2A2109",
          "onSecondary": "#FFFFFF",
          "secondaryContainer": "#4C4226",
          "onSecondaryContainer": "#FFFFFF",
          "tertiary": "#092812",
          "onTertiary": "#FFFFFF",
          "tertiaryContainer": "#2B4930",
          "onTertiaryContainer": "#FFFFFF",
          "error": "#4E0002",
          "onError": "#FFFFFF",
          "errorContainer": "#8C0009",
          "onErrorContainer": "#FFFFFF",
          "background": "#FFF8F1",
          "onBackground": "#1F1B13",
          "surface": "#FFF8F1",
          "onSurface": "#000000",
          "surfaceVariant": "#EBE1CF",
          "onSurfaceVariant": "#282318",
          "outline": "#484235",
          "outlineVariant": "#484235",
          "shadow": "#000000",
          "scrim": "#000000",
          "inverseSurface": "#343027",
          "inverseOnSurface": "#FFFFFF",
          "inversePrimary": "#FFEAB8",
          "primaryFixed": "#534100",
          "onPrimaryFixed": "#FFFFFF",
          "primaryFixedDim": "#382B00",
          "onPrimaryFixedVariant": "#FFFFFF",
          "secondaryFixed": "#4C4226",
          "onSecondaryFixed": "#FFFFFF",
          "secondaryFixedDim": "#352C12",
          "onSecondaryFixedVariant": "#FFFFFF",
          "tertiaryFixed": "#2B4930",
          "onTertiaryFixed": "#FFFFFF",
          "tertiaryFixedDim": "#15331C",
          "onTertiaryFixedVariant": "#FFFFFF",
          "surfaceDim": "#E1D9CC",
          "surfaceBright": "#FFF8F1",
          "surfaceContainerLowest": "#FFFFFF",
          "surfaceContainerLow": "#FBF3E5",
          "surfaceContainer": "#F5EDDF",
          "surfaceContainerHigh": "#F0E7D9",
          "surfaceContainerHighest": "#EAE1D4"
      },
      "dark": {
          "primary": "#E2C46D",
          "surfaceTint": "#E2C46D",
          "onPrimary": "#3D2F00",
          "primaryContainer": "#574400",
          "onPrimaryContainer": "#FFE08A",
          "secondary": "#D5C5A1",
          "onSecondary": "#393015",
          "secondaryContainer": "#50462A",
          "onSecondaryContainer": "#F2E1BB",
          "tertiary": "#ADCFAE",
          "onTertiary": "#19361F",
          "tertiaryContainer": "#2F4D34",
          "onTertiaryContainer": "#C8ECC9",
          "error": "#FFB4AB",
          "onError": "#690005",
          "errorContainer": "#93000A",
          "onErrorContainer": "#FFDAD6",
          "background": "#16130B",
          "onBackground": "#EAE1D4",
          "surface": "#16130B",
          "onSurface": "#EAE1D4",
          "surfaceVariant": "#4C4639",
          "onSurfaceVariant": "#CFC6B4",
          "outline": "#989080",
          "outlineVariant": "#4C4639",
          "shadow": "#000000",
          "scrim": "#000000",
          "inverseSurface": "#EAE1D4",
          "inverseOnSurface": "#343027",
          "inversePrimary": "#735C0C",
          "primaryFixed": "#FFE08A",
          "onPrimaryFixed": "#241A00",
          "primaryFixedDim": "#E2C46D",
          "onPrimaryFixedVariant": "#574400",
          "secondaryFixed": "#F2E1BB",
          "onSecondaryFixed": "#231B04",
          "secondaryFixedDim": "#D5C5A1",
          "onSecondaryFixedVariant": "#50462A",
          "tertiaryFixed": "#C8ECC9",
          "onTertiaryFixed": "#03210C",
          "tertiaryFixedDim": "#ADCFAE",
          "onTertiaryFixedVariant": "#2F4D34",
          "surfaceDim": "#16130B",
          "surfaceBright": "#3D392F",
          "surfaceContainerLowest": "#110E07",
          "surfaceContainerLow": "#1F1B13",
          "surfaceContainer": "#231F17",
          "surfaceContainerHigh": "#2D2A21",
          "surfaceContainerHighest": "#38342B"
      },
      "dark-medium-contrast": {
          "primary": "#E7C870",
          "surfaceTint": "#E2C46D",
          "onPrimary": "#1D1500",
          "primaryContainer": "#A98E3D",
          "onPrimaryContainer": "#000000",
          "secondary": "#D9C9A5",
          "onSecondary": "#1D1501",
          "secondaryContainer": "#9D906E",
          "onSecondaryContainer": "#000000",
          "tertiary": "#B1D4B2",
          "onTertiary": "#001B07",
          "tertiaryContainer": "#78997A",
          "onTertiaryContainer": "#000000",
          "error": "#FFBAB1",
          "onError": "#370001",
          "errorContainer": "#FF5449",
          "onErrorContainer": "#000000",
          "background": "#16130B",
          "onBackground": "#EAE1D4",
          "surface": "#16130B",
          "onSurface": "#FFFAF6",
          "surfaceVariant": "#4C4639",
          "onSurfaceVariant": "#D3CAB8",
          "outline": "#AAA291",
          "outlineVariant": "#8A8273",
          "shadow": "#000000",
          "scrim": "#000000",
          "inverseSurface": "#EAE1D4",
          "inverseOnSurface": "#2D2A21",
          "inversePrimary": "#594600",
          "primaryFixed": "#FFE08A",
          "onPrimaryFixed": "#171000",
          "primaryFixedDim": "#E2C46D",
          "onPrimaryFixedVariant": "#443400",
          "secondaryFixed": "#F2E1BB",
          "onSecondaryFixed": "#171000",
          "secondaryFixedDim": "#D5C5A1",
          "onSecondaryFixedVariant": "#3F351B",
          "tertiaryFixed": "#C8ECC9",
          "onTertiaryFixed": "#001505",
          "tertiaryFixedDim": "#ADCFAE",
          "onTertiaryFixedVariant": "#1F3C24",
          "surfaceDim": "#16130B",
          "surfaceBright": "#3D392F",
          "surfaceContainerLowest": "#110E07",
          "surfaceContainerLow": "#1F1B13",
          "surfaceContainer": "#231F17",
          "surfaceContainerHigh": "#2D2A21",
          "surfaceContainerHighest": "#38342B"
      },
      "dark-high-contrast": {
          "primary": "#FFFAF6",
          "surfaceTint": "#E2C46D",
          "onPrimary": "#000000",
          "primaryContainer": "#E7C870",
          "onPrimaryContainer": "#000000",
          "secondary": "#FFFAF6",
          "onSecondary": "#000000",
          "secondaryContainer": "#D9C9A5",
          "onSecondaryContainer": "#000000",
          "tertiary": "#F0FFED",
          "onTertiary": "#000000",
          "tertiaryContainer": "#B1D4B2",
          "onTertiaryContainer": "#000000",
          "error": "#FFF9F9",
          "onError": "#000000",
          "errorContainer": "#FFBAB1",
          "onErrorContainer": "#000000",
          "background": "#16130B",
          "onBackground": "#EAE1D4",
          "surface": "#16130B",
          "onSurface": "#FFFFFF",
          "surfaceVariant": "#4C4639",
          "onSurfaceVariant": "#FFFAF6",
          "outline": "#D3CAB8",
          "outlineVariant": "#D3CAB8",
          "shadow": "#000000",
          "scrim": "#000000",
          "inverseSurface": "#EAE1D4",
          "inverseOnSurface": "#000000",
          "inversePrimary": "#352800",
          "primaryFixed": "#FFE5A0",
          "onPrimaryFixed": "#000000",
          "primaryFixedDim": "#E7C870",
          "onPrimaryFixedVariant": "#1D1500",
          "secondaryFixed": "#F6E5BF",
          "onSecondaryFixed": "#000000",
          "secondaryFixedDim": "#D9C9A5",
          "onSecondaryFixedVariant": "#1D1501",
          "tertiaryFixed": "#CCF0CD",
          "onTertiaryFixed": "#000000",
          "tertiaryFixedDim": "#B1D4B2",
          "onTertiaryFixedVariant": "#001B07",
          "surfaceDim": "#16130B",
          "surfaceBright": "#3D392F",
          "surfaceContainerLowest": "#110E07",
          "surfaceContainerLow": "#1F1B13",
          "surfaceContainer": "#231F17",
          "surfaceContainerHigh": "#2D2A21",
          "surfaceContainerHighest": "#38342B"
      }
  },
  "palettes": {
      "primary": {
          "0": "#000000",
          "5": "#171000",
          "10": "#241A00",
          "15": "#302400",
          "20": "#3D2F00",
          "25": "#4A3900",
          "30": "#574400",
          "35": "#655008",
          "40": "#725C16",
          "50": "#8C752D",
          "60": "#A88E44",
          "70": "#C4A95B",
          "80": "#E1C474",
          "90": "#FEE08C",
          "95": "#FFEFCC",
          "98": "#FFF8F1",
          "99": "#FFFBFF",
          "100": "#FFFFFF"
      },
      "secondary": {
          "0": "#000000",
          "5": "#161003",
          "10": "#211B0B",
          "15": "#2B2514",
          "20": "#36301E",
          "25": "#423B28",
          "30": "#4E4633",
          "35": "#5A523E",
          "40": "#665E49",
          "50": "#7F7661",
          "60": "#9A9079",
          "70": "#B5AA92",
          "80": "#D1C5AC",
          "90": "#EEE1C7",
          "95": "#FCF0D5",
          "98": "#FFF8F1",
          "99": "#FFFBFF",
          "100": "#FFFFFF"
      },
      "tertiary": {
          "0": "#000000",
          "5": "#041407",
          "10": "#0E1F11",
          "15": "#182A1B",
          "20": "#223525",
          "25": "#2D402F",
          "30": "#384B3A",
          "35": "#445745",
          "40": "#506351",
          "50": "#687C69",
          "60": "#819681",
          "70": "#9BB19B",
          "80": "#B7CCB6",
          "90": "#D2E8D1",
          "95": "#E0F7DF",
          "98": "#EAFFE8",
          "99": "#F6FFF2",
          "100": "#FFFFFF"
      },
      "neutral": {
          "0": "#000000",
          "5": "#12110D",
          "10": "#1D1B18",
          "15": "#282522",
          "20": "#32302C",
          "25": "#3E3B37",
          "30": "#494642",
          "35": "#55524D",
          "40": "#615E59",
          "50": "#7A7671",
          "60": "#94908B",
          "70": "#AFAAA5",
          "80": "#CBC6C0",
          "90": "#E7E2DB",
          "95": "#F6F0E9",
          "98": "#FFF8F2",
          "99": "#FFFBFF",
          "100": "#FFFFFF"
      },
      "neutral-variant": {
          "0": "#000000",
          "5": "#13110A",
          "10": "#1E1B14",
          "15": "#29251D",
          "20": "#343028",
          "25": "#3F3B32",
          "30": "#4B463D",
          "35": "#575248",
          "40": "#635E54",
          "50": "#7C766C",
          "60": "#969085",
          "70": "#B1AA9F",
          "80": "#CDC6BA",
          "90": "#E9E1D5",
          "95": "#F8F0E3",
          "98": "#FFF8F1",
          "99": "#FFFBFF",
          "100": "#FFFFFF"
      }
  }
}
*/


// Figma generated palette
/*
"schemes": {
  "light": {
      "primary": "#745B0B",
      "surfaceTint": "#745B0B",
      "onPrimary": "#FFFFFF",
      "primaryContainer": "#FFDF92",
      "onPrimaryContainer": "#241A00",
      "secondary": "#6A5D3F",
      "onSecondary": "#FFFFFF",
      "secondaryContainer": "#F3E1BB", 
      "onSecondaryContainer": "#231A04",
      "tertiary": "#486549",
      "onTertiary": "#FFFFFF",
      "tertiaryContainer": "#C9EBC7",
      "onTertiaryContainer": "#04210B",
      "error": "#BA1A1A",
      "onError": "#FFFFFF",
      "errorContainer": "#FFDAD6",
      "onErrorContainer": "#410002",
      "background": "#FFF8F1",
      "onBackground": "#1F1B13",
      "surface": "#FFF8F1",
      "onSurface": "#1F1B13",
      "surfaceVariant": "#ECE1CF",
      "onSurfaceVariant": "#4C4639",
      "outline": "#7E7667",
      "outlineVariant": "#CFC5B4",
      "shadow": "#000000",
      "scrim": "#000000",
      "inverseSurface": "#343027",
      "inverseOnSurface": "#F9F0E2",
      "inversePrimary": "#E5C36C",
      "primaryFixed": "#FFDF92",
      "onPrimaryFixed": "#241A00",
      "primaryFixedDim": "#E5C36C",
      "onPrimaryFixedVariant": "#594400",
      "secondaryFixed": "#F3E1BB",
      "onSecondaryFixed": "#231A04",
      "secondaryFixedDim": "#D6C5A0",
      "onSecondaryFixedVariant": "#51462A",
      "tertiaryFixed": "#C9EBC7",
      "onTertiaryFixed": "#04210B",
      "tertiaryFixedDim": "#AECFAC",
      "onTertiaryFixedVariant": "#314D33",
      "surfaceDim": "#E2D9CC",
      "surfaceBright": "#FFF8F1",
      "surfaceContainerLowest": "#FFFFFF",
      "surfaceContainerLow": "#FCF2E5",
      "surfaceContainer": "#F6EDDF",
      "surfaceContainerHigh": "#F0E7D9",
      "surfaceContainerHighest": "#EAE1D4"
  },
  "light-medium-contrast": {
      "primary": "#544000",
      "surfaceTint": "#745B0B",
      "onPrimary": "#FFFFFF",
      "primaryContainer": "#8D7123",
      "onPrimaryContainer": "#FFFFFF",
      "secondary": "#4D4226",
      "onSecondary": "#FFFFFF",
      "secondaryContainer": "#817354",
      "onSecondaryContainer": "#FFFFFF",
      "tertiary": "#2D492F",
      "onTertiary": "#FFFFFF",
      "tertiaryContainer": "#5E7C5E",
      "onTertiaryContainer": "#FFFFFF",
      "error": "#8C0009",
      "onError": "#FFFFFF",
      "errorContainer": "#DA342E",
      "onErrorContainer": "#FFFFFF",
      "background": "#FFF8F1",
      "onBackground": "#1F1B13",
      "surface": "#FFF8F1",
      "onSurface": "#1F1B13",
      "surfaceVariant": "#ECE1CF",
      "onSurfaceVariant": "#484235",
      "outline": "#655E50",
      "outlineVariant": "#827A6B",
      "shadow": "#000000",
      "scrim": "#000000",
      "inverseSurface": "#343027",
      "inverseOnSurface": "#F9F0E2",
      "inversePrimary": "#E5C36C",
      "primaryFixed": "#8D7123",
      "onPrimaryFixed": "#FFFFFF",
      "primaryFixedDim": "#725908",
      "onPrimaryFixedVariant": "#FFFFFF",
      "secondaryFixed": "#817354",
      "onSecondaryFixed": "#FFFFFF",
      "secondaryFixedDim": "#675B3D",
      "onSecondaryFixedVariant": "#FFFFFF",
      "tertiaryFixed": "#5E7C5E",
      "onTertiaryFixed": "#FFFFFF",
      "tertiaryFixedDim": "#456347",
      "onTertiaryFixedVariant": "#FFFFFF",
      "surfaceDim": "#E2D9CC",
      "surfaceBright": "#FFF8F1",
      "surfaceContainerLowest": "#FFFFFF",
      "surfaceContainerLow": "#FCF2E5",
      "surfaceContainer": "#F6EDDF",
      "surfaceContainerHigh": "#F0E7D9",
      "surfaceContainerHighest": "#EAE1D4"
  },
  "light-high-contrast": {
      "primary": "#2C2100",
      "surfaceTint": "#745B0B",
      "onPrimary": "#FFFFFF",
      "primaryContainer": "#544000",
      "onPrimaryContainer": "#FFFFFF",
      "secondary": "#2A2109",
      "onSecondary": "#FFFFFF",
      "secondaryContainer": "#4D4226",
      "onSecondaryContainer": "#FFFFFF",
      "tertiary": "#0B2811",
      "onTertiary": "#FFFFFF",
      "tertiaryContainer": "#2D492F",
      "onTertiaryContainer": "#FFFFFF",
      "error": "#4E0002",
      "onError": "#FFFFFF",
      "errorContainer": "#8C0009",
      "onErrorContainer": "#FFFFFF",
      "background": "#FFF8F1",
      "onBackground": "#1F1B13",
      "surface": "#FFF8F1",
      "onSurface": "#000000",
      "surfaceVariant": "#ECE1CF",
      "onSurfaceVariant": "#282318",
      "outline": "#484235",
      "outlineVariant": "#484235",
      "shadow": "#000000",
      "scrim": "#000000",
      "inverseSurface": "#343027",
      "inverseOnSurface": "#FFFFFF",
      "inversePrimary": "#FFEABC",
      "primaryFixed": "#544000",
      "onPrimaryFixed": "#FFFFFF",
      "primaryFixedDim": "#392B00",
      "onPrimaryFixedVariant": "#FFFFFF",
      "secondaryFixed": "#4D4226",
      "onSecondaryFixed": "#FFFFFF",
      "secondaryFixedDim": "#352C12",
      "onSecondaryFixedVariant": "#FFFFFF",
      "tertiaryFixed": "#2D492F",
      "onTertiaryFixed": "#FFFFFF",
      "tertiaryFixedDim": "#16331A",
      "onTertiaryFixedVariant": "#FFFFFF",
      "surfaceDim": "#E2D9CC",
      "surfaceBright": "#FFF8F1",
      "surfaceContainerLowest": "#FFFFFF",
      "surfaceContainerLow": "#FCF2E5",
      "surfaceContainer": "#F6EDDF",
      "surfaceContainerHigh": "#F0E7D9",
      "surfaceContainerHighest": "#EAE1D4"
  },
  "dark": {
      "primary": "#E5C36C",
      "surfaceTint": "#E5C36C",
      "onPrimary": "#3E2E00",
      "primaryContainer": "#594400",
      "onPrimaryContainer": "#FFDF92",
      "secondary": "#D6C5A0",
      "onSecondary": "#392F15",
      "secondaryContainer": "#51462A",
      "onSecondaryContainer": "#F3E1BB",
      "tertiary": "#AECFAC",
      "onTertiary": "#1A361E",
      "tertiaryContainer": "#314D33",
      "onTertiaryContainer": "#C9EBC7",
      "error": "#FFB4AB",
      "onError": "#690005",
      "errorContainer": "#93000A",
      "onErrorContainer": "#FFDAD6",
      "background": "#16130B",
      "onBackground": "#EAE1D4",
      "surface": "#16130B",
      "onSurface": "#EAE1D4",
      "surfaceVariant": "#4C4639",
      "onSurfaceVariant": "#CFC5B4",
      "outline": "#989080",
      "outlineVariant": "#4C4639",
      "shadow": "#000000",
      "scrim": "#000000",
      "inverseSurface": "#EAE1D4",
      "inverseOnSurface": "#343027",
      "inversePrimary": "#745B0B",
      "primaryFixed": "#FFDF92",
      "onPrimaryFixed": "#241A00",
      "primaryFixedDim": "#E5C36C",
      "onPrimaryFixedVariant": "#594400",
      "secondaryFixed": "#F3E1BB",
      "onSecondaryFixed": "#231A04",
      "secondaryFixedDim": "#D6C5A0",
      "onSecondaryFixedVariant": "#51462A",
      "tertiaryFixed": "#C9EBC7",
      "onTertiaryFixed": "#04210B",
      "tertiaryFixedDim": "#AECFAC",
      "onTertiaryFixedVariant": "#314D33",
      "surfaceDim": "#16130B",
      "surfaceBright": "#3D392F",
      "surfaceContainerLowest": "#110E07",
      "surfaceContainerLow": "#1F1B13",
      "surfaceContainer": "#231F17",
      "surfaceContainerHigh": "#2E2A21",
      "surfaceContainerHighest": "#39342B"
  },
  "dark-medium-contrast": {
      "primary": "#E9C770",
      "surfaceTint": "#E5C36C",
      "onPrimary": "#1E1500",
      "primaryContainer": "#AB8D3D",
      "onPrimaryContainer": "#000000",
      "secondary": "#DAC9A4",
      "onSecondary": "#1D1501",
      "secondaryContainer": "#9E8F6E",
      "onSecondaryContainer": "#000000",
      "tertiary": "#B2D3B0",
      "onTertiary": "#011B06",
      "tertiaryContainer": "#799979",
      "onTertiaryContainer": "#000000",
      "error": "#FFBAB1",
      "onError": "#370001",
      "errorContainer": "#FF5449",
      "onErrorContainer": "#000000",
      "background": "#16130B",
      "onBackground": "#EAE1D4",
      "surface": "#16130B",
      "onSurface": "#FFFAF6",
      "surfaceVariant": "#4C4639",
      "onSurfaceVariant": "#D4CAB8",
      "outline": "#ABA291",
      "outlineVariant": "#8A8273",
      "shadow": "#000000",
      "scrim": "#000000",
      "inverseSurface": "#EAE1D4",
      "inverseOnSurface": "#2E2A21",
      "inversePrimary": "#5A4500",
      "primaryFixed": "#FFDF92",
      "onPrimaryFixed": "#181000",
      "primaryFixedDim": "#E5C36C",
      "onPrimaryFixedVariant": "#453400",
      "secondaryFixed": "#F3E1BB",
      "onSecondaryFixed": "#181000",
      "secondaryFixedDim": "#D6C5A0",
      "onSecondaryFixedVariant": "#3F351B",
      "tertiaryFixed": "#C9EBC7",
      "onTertiaryFixed": "#001504",
      "tertiaryFixedDim": "#AECFAC",
      "onTertiaryFixedVariant": "#203C23",
      "surfaceDim": "#16130B",
      "surfaceBright": "#3D392F",
      "surfaceContainerLowest": "#110E07",
      "surfaceContainerLow": "#1F1B13",
      "surfaceContainer": "#231F17",
      "surfaceContainerHigh": "#2E2A21",
      "surfaceContainerHighest": "#39342B"
  },
  "dark-high-contrast": {
      "primary": "#FFFAF6",
      "surfaceTint": "#E5C36C",
      "onPrimary": "#000000",
      "primaryContainer": "#E9C770",
      "onPrimaryContainer": "#000000",
      "secondary": "#FFFAF6",
      "onSecondary": "#000000",
      "secondaryContainer": "#DAC9A4",
      "onSecondaryContainer": "#000000",
      "tertiary": "#F0FFEC",
      "onTertiary": "#000000",
      "tertiaryContainer": "#B2D3B0",
      "onTertiaryContainer": "#000000",
      "error": "#FFF9F9",
      "onError": "#000000",
      "errorContainer": "#FFBAB1",
      "onErrorContainer": "#000000",
      "background": "#16130B",
      "onBackground": "#EAE1D4",
      "surface": "#16130B",
      "onSurface": "#FFFFFF",
      "surfaceVariant": "#4C4639",
      "onSurfaceVariant": "#FFFAF6",
      "outline": "#D4CAB8",
      "outlineVariant": "#D4CAB8",
      "shadow": "#000000",
      "scrim": "#000000",
      "inverseSurface": "#EAE1D4",
      "inverseOnSurface": "#000000",
      "inversePrimary": "#362800",
      "primaryFixed": "#FFE4A6",
      "onPrimaryFixed": "#000000",
      "primaryFixedDim": "#E9C770",
      "onPrimaryFixedVariant": "#1E1500",
      "secondaryFixed": "#F7E5BF",
      "onSecondaryFixed": "#000000",
      "secondaryFixedDim": "#DAC9A4",
      "onSecondaryFixedVariant": "#1D1501",
      "tertiaryFixed": "#CEF0CC",
      "onTertiaryFixed": "#000000",
      "tertiaryFixedDim": "#B2D3B0",
      "onTertiaryFixedVariant": "#011B06",
      "surfaceDim": "#16130B",
      "surfaceBright": "#3D392F",
      "surfaceContainerLowest": "#110E07",
      "surfaceContainerLow": "#1F1B13",
      "surfaceContainer": "#231F17",
      "surfaceContainerHigh": "#2E2A21",
      "surfaceContainerHighest": "#39342B"
  }
},
"palettes": {
  "primary": {
      "0": "#000000",
      "5": "#171000",
      "10": "#241A00",
      "15": "#312400",
      "20": "#3E2E00",
      "25": "#4B3900",
      "30": "#594400",
      "35": "#674F00",
      "40": "#745B0D",
      "50": "#8F7426",
      "60": "#AB8D3E",
      "70": "#C7A855",
      "80": "#E5C36D",
      "90": "#FFDF92",
      "95": "#FFEFD0",
      "98": "#FFF8F1",
      "99": "#FFFBFF",
      "100": "#FFFFFF"
  },
  "secondary": {
      "0": "#000000",
      "5": "#161003",
      "10": "#211B0A",
      "15": "#2C2513",
      "20": "#37301D",
      "25": "#433B27",
      "30": "#4F4632",
      "35": "#5B513C",
      "40": "#675D48",
      "50": "#81765F",
      "60": "#9B9077",
      "70": "#B6AA90",
      "80": "#D2C5AB",
      "90": "#EFE1C5",
      "95": "#FEEFD3",
      "98": "#FFF8F1",
      "99": "#FFFBFF",
      "100": "#FFFFFF"
  },
  "tertiary": {
      "0": "#000000",
      "5": "#041506",
      "10": "#0D1F0F",
      "15": "#172A19",
      "20": "#223523",
      "25": "#2D402D",
      "30": "#384C38",
      "35": "#435743",
      "40": "#4F644F",
      "50": "#677D67",
      "60": "#81967F",
      "70": "#9BB199",
      "80": "#B6CDB3",
      "90": "#D2E9CE",
      "95": "#E0F7DC",
      "98": "#EBFFE7",
      "99": "#F6FFF1",
      "100": "#FFFFFF"
  },
  "neutral": {
      "0": "#000000",
      "5": "#13110D",
      "10": "#1D1B17",
      "15": "#282521",
      "20": "#33302C",
      "25": "#3E3B36",
      "30": "#494642",
      "35": "#55524D",
      "40": "#615E59",
      "50": "#7A7671",
      "60": "#95908A",
      "70": "#AFAAA4",
      "80": "#CBC6BF",
      "90": "#E8E1DB",
      "95": "#F6F0E9",
      "98": "#FFF8F1",
      "99": "#FFFBFF",
      "100": "#FFFFFF"
  },
  "neutral-variant": {
      "0": "#000000",
      "5": "#141109",
      "10": "#1F1B13",
      "15": "#29251D",
      "20": "#343027",
      "25": "#403B32",
      "30": "#4B463C",
      "35": "#575248",
      "40": "#635E53",
      "50": "#7D766B",
      "60": "#979084",
      "70": "#B2AA9E",
      "80": "#CEC5B9",
      "90": "#EAE1D4",
      "95": "#F9F0E2",
      "98": "#FFF8F1",
      "99": "#FFFBFF",
      "100": "#FFFFFF"
  }
}
}
*/